import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  CheckCircle,
  XCircle,
  Clock,
  Pause,
  Play,
  ArrowUpDown,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';

const EnrollmentRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/enrollment-requests?status=${statusFilter}`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await api.put(`/admin/enrollment-requests/${requestId}/approve`);
      toast.success('Request approved');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await api.put(`/admin/enrollment-requests/${selectedRequest.id}/reject`, { reason: rejectReason });
      toast.success('Request rejected and parent notified');
      setRejectDialogOpen(false);
      setSelectedRequest(null);
      setRejectReason('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const getTypeIcon = (type) => {
    const icons = {
      pause: <Pause className="h-4 w-4 text-amber-500" />,
      resume: <Play className="h-4 w-4 text-green-500" />,
      plan_change: <ArrowUpDown className="h-4 w-4 text-blue-500" />,
      cancel: <XCircle className="h-4 w-4 text-rose-500" />
    };
    return icons[type] || <Clock className="h-4 w-4 text-slate-400" />;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-amber-100 text-amber-700', icon: Clock },
      approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      rejected: { color: 'bg-rose-100 text-rose-700', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatRequestDetails = (req) => {
    switch (req.type) {
      case 'pause':
        return (
          <div className="text-sm">
            <p>Duration: {req.pause_weeks} weeks</p>
            <p className="text-green-600">Credit: {cs()}{req.credit_amount?.toFixed(2)}</p>
          </div>
        );
      case 'resume':
        return (
          <div className="text-sm">
            <p>Resume Date: {new Date(req.resume_date).toLocaleDateString()}</p>
            <p>Prorated: {cs()}{req.prorated_amount?.toFixed(2)}</p>
            <p className="text-green-600">Credit Applied: {cs()}{req.credit_applied?.toFixed(2)}</p>
            <p className="font-medium">Amount Due: {cs()}{req.amount_due?.toFixed(2)}</p>
          </div>
        );
      case 'plan_change':
        return (
          <div className="text-sm">
            <p>{req.current_frequency} → {req.new_frequency}</p>
            <p>{cs()}{req.current_amount?.toFixed(2)} → {cs()}{req.new_amount?.toFixed(2)}</p>
            <p>Effective: {new Date(req.effective_date).toLocaleDateString()}</p>
          </div>
        );
      case 'cancel':
        return (
          <div className="text-sm">
            <p>Effective: {new Date(req.effective_date || req.cancel_effective_date).toLocaleDateString()}</p>
            {req.reason && <p className="text-slate-500">Reason: {req.reason}</p>}
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Enrollment Requests">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Enrollment Requests">
      <div className="space-y-6" data-testid="enrollment-requests-page">
        <div className="flex justify-between items-center">
          <p className="text-slate-600">Review and approve enrollment changes</p>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card className="border border-slate-200">
          <CardContent className="p-0">
            {requests.length === 0 ? (
              <div className="p-12 text-center">
                <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No {statusFilter} requests</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Parent</TableHead>
                      <TableHead>Player</TableHead>
                      <TableHead>Program</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Status</TableHead>
                      {statusFilter === 'pending' && <TableHead className="w-32">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((req) => (
                      <TableRow key={req.id} data-testid={`request-${req.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getTypeIcon(req.type)}
                            <span className="capitalize">{req.type.replace('_', ' ')}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{req.parent_name}</p>
                            <p className="text-sm text-slate-500">{req.parent_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{req.player_name}</TableCell>
                        <TableCell>
                          <span className="text-sm font-medium text-slate-700">
                            {req.program_name || '-'}
                          </span>
                        </TableCell>
                        <TableCell>{formatRequestDetails(req)}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {(() => {
                            const dateStr = req.requested_at || req.created_at;
                            if (!dateStr) return '-';
                            try {
                              const date = new Date(dateStr);
                              if (isNaN(date.getTime())) return '-';
                              return date.toLocaleDateString();
                            } catch {
                              return '-';
                            }
                          })()}
                        </TableCell>
                        <TableCell>{getStatusBadge(req.status)}</TableCell>
                        {statusFilter === 'pending' && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApprove(req.id)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`approve-${req.id}`}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openRejectDialog(req)}
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                data-testid={`reject-${req.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-slate-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pause Requests</p>
                <p className="font-heading text-2xl font-bold text-amber-600">
                  {requests.filter(r => r.type === 'pause').length}
                </p>
              </div>
              <Pause className="h-8 w-8 text-amber-400" />
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Resume Requests</p>
                <p className="font-heading text-2xl font-bold text-green-600">
                  {requests.filter(r => r.type === 'resume').length}
                </p>
              </div>
              <Play className="h-8 w-8 text-green-400" />
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Plan Changes</p>
                <p className="font-heading text-2xl font-bold text-blue-600">
                  {requests.filter(r => r.type === 'plan_change').length}
                </p>
              </div>
              <ArrowUpDown className="h-8 w-8 text-blue-400" />
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Cancellations</p>
                <p className="font-heading text-2xl font-bold text-rose-600">
                  {requests.filter(r => r.type === 'cancel').length}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-rose-400" />
            </CardContent>
          </Card>
        </div>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={(open) => {
          setRejectDialogOpen(open);
          if (!open) {
            setRejectReason('');
            setSelectedRequest(null);
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <XCircle className="h-5 w-5 text-rose-500" />
                Reject Request
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedRequest?.player_name}</p>
                <p className="text-sm text-slate-500">Request Type: {selectedRequest?.type?.replace('_', ' ')}</p>
                <p className="text-sm text-slate-500">Parent: {selectedRequest?.parent_name}</p>
              </div>
              
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  The parent will receive an email notification with the rejection reason.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-rose-600">Rejection Reason *</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please explain why this request is being rejected..."
                  rows={4}
                  className="border-rose-200 focus:border-rose-400"
                  data-testid="reject-reason-input"
                />
              </div>
              
              <DialogFooter className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-rose-500 hover:bg-rose-600"
                  onClick={handleReject}
                  disabled={!rejectReason.trim()}
                  data-testid="confirm-reject-btn"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject Request
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default EnrollmentRequests;
